/**
 * LotesPro Map Style Configuration
 *
 * Este archivo genera el estilo de mapa completo para el proyecto,
 * combinando el JSON base (desde URL remota) con las URLs dinámicas del brandConfig.
 *
 * Similar a https://www.openhistoricalmap.org/map-styles/historical/historical.json
 *
 * @usage
 * import { getMapStyle, getMapStyleWithProject, loadMapStyle } from '../config/mapStyleConfig';
 *
 * // Cargar estilo base (async) - REQUERIDO antes de usar getMapStyle
 * await loadMapStyle();
 *
 * // Estilo base (sin filtro de proyecto)
 * const style = getMapStyle();
 *
 * // Estilo filtrado por proyecto
 * const styleWithProject = getMapStyleWithProject(projectId);
 */

import brandConfig, { getTileUrl } from './brandConfig';

// Cache del estilo cargado
let cachedBaseStyle = null;
let isLoading = false;
let loadPromise = null;

/**
 * Limpia el cache del estilo para forzar recarga
 */
export function clearMapStyleCache() {
  cachedBaseStyle = null;
  loadPromise = null;
  isLoading = false;
}

/**
 * Carga el estilo base desde la URL remota
 * @param {boolean} forceRefresh - Forzar recarga ignorando cache
 * @returns {Promise<Object>} El estilo base cargado
 */
export async function loadMapStyle(forceRefresh = false) {
  // En desarrollo, siempre cargar fresco
  const isDevelopment = process.env.NODE_ENV === 'development' ||
                        window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';

  const shouldRefresh = forceRefresh || isDevelopment;

  // Limpiar cache si necesitamos refrescar
  if (shouldRefresh) {
    cachedBaseStyle = null;
    loadPromise = null;
    isLoading = false;
  }

  // Si ya está cargado y no necesitamos refrescar, retornar el cache
  if (cachedBaseStyle && !shouldRefresh) {
    return cachedBaseStyle;
  }

  // Si ya está cargando, esperar la promesa existente
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  isLoading = true;
  loadPromise = (async () => {
    try {
      // Agregar cache-buster para evitar cache del navegador
      const cacheBuster = `?t=${Date.now()}`;
      const url = brandConfig.mapStyleUrl + cacheBuster;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        cache: 'no-store', // Forzar bypass del cache del navegador
      });

      if (!response.ok) {
        throw new Error(`Failed to load map style: ${response.status}`);
      }
      let styleText = await response.text();
      // Replace token placeholders with actual values from brandConfig
      styleText = styleText.replace(/\$\{MAPBOX_TOKEN\}/g, brandConfig.mapboxToken);
      cachedBaseStyle = JSON.parse(styleText);
      return cachedBaseStyle;
    } catch (error) {
      console.error('[MapStyle] Failed to load map style:', error.message);
      throw error;
    } finally {
      isLoading = false;
    }
  })();

  return loadPromise;
}

/**
 * Obtiene el estilo base (sincrónico, requiere haber llamado loadMapStyle primero)
 */
function getBaseStyle() {
  if (!cachedBaseStyle) {
    throw new Error('[MapStyle] Style not loaded. Call loadMapStyle() first.');
  }
  return cachedBaseStyle;
}

/**
 * Genera las fuentes (sources) con las URLs del tiler, filtradas por project_id
 * @param {string} projectId - ID del proyecto para filtrar tiles desde el servidor (REQUERIDO)
 */
function generateSources(projectId) {
  if (!projectId) {
    console.warn('[MapStyle] generateSources called without projectId - tiles will not be filtered!');
  }

  const tilerTiles = brandConfig.tilerTiles;
  const baseStyle = getBaseStyle();

  return {
    ...baseStyle.sources,
    'cloud4geo': {
      type: 'vector',
      tiles: [getTileUrl(tilerTiles.lotesAreas, projectId)],
      minzoom: 10,
      maxzoom: 22,
      promoteId: { 'lotes_areas': 'id' }
    },
    'lotes-centroid': {
      type: 'vector',
      tiles: [getTileUrl(tilerTiles.lotesCentroid, projectId)],
      minzoom: 14,
      maxzoom: 22
    },
    'lotespro-areas': {
      type: 'vector',
      tiles: [getTileUrl(tilerTiles.lotesproAreas, projectId)],
      minzoom: 10,
      maxzoom: 22
    },
    'lotespro-areas-centroid': {
      type: 'vector',
      tiles: [getTileUrl(tilerTiles.lotesproAreasCentroid, projectId)],
      minzoom: 14,
      maxzoom: 22
    },
    'lotespro-lines': {
      type: 'vector',
      tiles: [getTileUrl(tilerTiles.lotesproLines, projectId)],
      minzoom: 10,
      maxzoom: 22
    },
    'lotespro-markers': {
      type: 'vector',
      tiles: [getTileUrl(tilerTiles.lotesproMarkers, projectId)],
      minzoom: 10,
      maxzoom: 22
    }
  };
}

/**
 * Aplica filtro de proyecto a las capas que lo requieren
 */
function applyProjectFilter(layers, projectId) {
  if (!projectId) return layers;

  const projectFilter = ['==', ['get', 'project_id'], projectId];

  return layers.map(layer => {
    // Capas que necesitan filtro de proyecto
    const layersNeedingFilter = [
      'lotes-layer-stroke',
      'lotes-layer-status',
      'lotes-layer-highlighted',
      'lotes-layer-highlight-stroke',
      'lotes-layer-labels',
      'lines-layer-casing',
      'lines-layer',
      'lines-layer-labels',
      'etapa-layer-fill',
      'etapa-layer-line',
      'areas-recreativas-layer-fill',
      'areas-recreativas-layer-line',
      'areas-recreativas-layer-labels',
      // Marker symbol layers
      'markers-arboles',
      'markers-ingreso',
      'markers-salida',
      'markers-estacionamiento',
      'markers-punto-foto',
      'markers-punto-interes',
      'markers-etapa-labels',
      'markers-bloque-labels'
    ];

    if (layersNeedingFilter.includes(layer.id)) {
      const existingFilter = layer.filter;
      const newFilter = existingFilter
        ? ['all', projectFilter, existingFilter]
        : projectFilter;

      return { ...layer, filter: newFilter };
    }

    return layer;
  });
}

/**
 * Genera el estilo completo del mapa
 * @param {string} [projectId] - ID del proyecto para filtrar tiles desde el servidor
 * @returns {Object} Estilo compatible con Mapbox/MapLibre GL
 */
export function getMapStyle(projectId) {
  const baseStyle = getBaseStyle();
  // Filtrar layers de Google/OSM (usados por dashboard), mantener solo Mapbox
  const layers = baseStyle.layers.filter(layer =>
    layer.id !== 'google-satellite-layer' && layer.id !== 'openstreetmap-layer'
  );
  return {
    ...baseStyle,
    layers,
    sprite: `${brandConfig.spritesUrl}/sprite`,
    sources: generateSources(projectId)
  };
}

/**
 * Genera el estilo con filtro de proyecto (server-side + client-side)
 * @param {string} projectId - ID del proyecto a filtrar
 * @returns {Object} Estilo compatible con Mapbox/MapLibre GL
 */
export function getMapStyleWithProject(projectId) {
  const style = getMapStyle(projectId);
  return {
    ...style,
    layers: applyProjectFilter(style.layers, projectId)
  };
}

export default {
  loadMapStyle,
  getMapStyle,
  getMapStyleWithProject,
  clearMapStyleCache
};
